import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { syntaxTree } from '@codemirror/language';
import { linter, Diagnostic } from '@codemirror/lint';
import { Extension, EditorState, StateField, RangeSetBuilder } from '@codemirror/state';
import { EditorView, DecorationSet, Decoration } from '@codemirror/view';
import { HTMLHint } from 'htmlhint';
import { JSHINT } from 'jshint';

import { Language, ReadOnlyRange } from './store/types';

export function baseThemeExtension(maxHeight): Extension {
  const baseTheme = EditorView.baseTheme({
    '&': {
      maxHeight: maxHeight,
      width: '100%',
      height: 'auto',
      border: '1px solid #d1d5db'
    },
    '.cm-scroller *': {
      fontFamily: "Menlo, Monaco, 'Courier New', monospace",
      fontSize: '14px'
    }
  });
  return baseTheme;
}

export function languageExtension(language: Language): Extension {
  switch (language) {
    case 'javascript':
      return [
        javascript(),
        jsLinter({
          esversion: 11,
          latedef: true,
          undef: true,
          browser: true,
          devel: true,
          asi: true,
          sub: true,
          globals: {
            //Globals for common tracking pixel window variables
            Bizible: true,
            dataLayer: true,
            fbq: true,
            twq: true
          }
        })
      ];
    case 'html':
      return [
        html(),
        htmlLinter({
          'tag-pair': true,
          'spec-char-escape': true,
          'id-unique': true,
          'src-not-empty': true,
          'attr-no-duplication': true
        })
      ];
    default:
      return [];
  }
}

export function restrictedKeywordsCheck(restrictedKeywords: String[]): Extension {
  return linter(view => {
    const diagnostics: Diagnostic[] = [];
    syntaxTree(view.state)
      .cursor()
      .iterate(node => {
        const text = view.state.doc.sliceString(node.from, node.to);
        if (restrictedKeywords.includes(text.toLowerCase())) {
          diagnostics.push({
            from: node.from,
            to: node.to,
            message: `Use of this keyword is not allowed: ${text}`,
            severity: 'error',
            actions: [
              {
                name: 'Remove',
                apply(view, from, to) {
                  view.dispatch({ changes: { from, to } });
                }
              }
            ]
          });
        }
      });
    return diagnostics;
  });
}

export function restrictedTagsCheck(restrictedTags: String[]): Extension {
  return linter(view => {
    const diagnostics: Diagnostic[] = [];
    const re = new RegExp(`<[/\\s]*(${restrictedTags.join('|')})(?=\\s|>)[^><]*>`, 'gi');
    let match;
    while ((match = re.exec(view.state.doc.toString())) !== null) {
      const { index } = match;
      diagnostics.push({
        from: index,
        to: index + match[0].length,
        message: `Use of this tag is not allowed: ${match[0]}`,
        severity: 'error',
        actions: [
          {
            name: 'Remove',
            apply(view, from, to) {
              view.dispatch({ changes: { from, to } });
            }
          }
        ]
      });
    }
    return diagnostics;
  });
}

function jsLinter(jsHintOptions): Extension {
  return linter(view => {
    const diagnostics: Diagnostic[] = [];
    JSHINT(view.state.doc.toString(), jsHintOptions);
    const errors = JSHINT.errors;
    if (errors.length > 0) {
      for (const error of errors) {
        let errorLength = 0;
        const { line, character, reason, a, evidence } = error;
        if (evidence && a && evidence.includes(a)) {
          errorLength = a.length;
        }
        diagnostics.push({
          from: view.state.doc.line(line).from + character - 1,
          to: view.state.doc.line(line).from + character - 1 + errorLength,
          message: reason,
          severity: 'error'
        });
      }
    }
    return diagnostics;
  });
}

export function htmlLinter(htmlHintRuleset): Extension {
  return linter(view => {
    const diagnostics: Diagnostic[] = [];
    const htmlHint = HTMLHint.verify(view.state.doc.toString() || '', htmlHintRuleset);
    if (htmlHint.length > 0) {
      for (const error of htmlHint) {
        const { line, col, message } = error;
        diagnostics.push({
          from: view.state.doc.line(line).from + col - 1,
          to: view.state.doc.line(line).from + col - 1,
          message,
          severity: 'error'
        });
      }
    }
    return diagnostics;
  });
}

export function readOnlyExtension(readOnlyRanges: ReadOnlyRange[]): Extension[] {
  const readOnlyField = defineReadOnlyField(readOnlyRanges);
  return [readOnlyField, readOnlyCheck(readOnlyField), readOnlyTheme()];
}

function readOnlyTheme(): Extension {
  return EditorView.theme({
    '.cm-readOnly, .cm-readOnly.cm-line span': { color: 'gray' }
  });
}

function defineReadOnlyField(ranges: ReadOnlyRange[]): StateField<DecorationSet> {
  const readOnlyDecoration = Decoration.line({
    class: 'cm-readOnly'
  });

  return StateField.define<DecorationSet>({
    create(state) {
      const builder = new RangeSetBuilder<Decoration>();
      for (let { fromLine, toLine } of ranges) {
        if (fromLine === 'last') {
          fromLine = state.doc.lines;
        }
        if (toLine === 'last') {
          toLine = state.doc.lines;
        }

        for (let line = fromLine; line <= toLine; line += 1) {
          const pos = state.doc.line(line).from;
          builder.add(pos, pos, readOnlyDecoration);
        }
      }
      return builder.finish();
    },
    update(readOnlyLines, tr) {
      readOnlyLines = readOnlyLines.map(tr.changes);
      return readOnlyLines;
    },
    provide: f => EditorView.decorations.from(f)
  });
}

function readOnlyCheck(readOnlyField: StateField<DecorationSet>): Extension {
  return EditorState.transactionFilter.of(tr => {
    const readOnlyRangeSet = tr.startState.field(readOnlyField, false);
    if (readOnlyRangeSet && tr.docChanged) {
      let block = false;
      tr.changes.iterChangedRanges((chFrom, chTo) => {
        const roIter = readOnlyRangeSet.iter();
        while (roIter.value) {
          const roFrom = tr.startState.doc.lineAt(roIter.from).from;
          const roTo = tr.startState.doc.lineAt(roIter.from).to;
          if (chTo >= roFrom && chFrom <= roTo) {
            block = true;
            return false;
          }
          roIter.next();
        }
      });
      if (block) {
        return [];
      }
    }
    return tr;
  });
}

export function characterLimitExtension(characterLimit: number): Extension {
  return EditorState.changeFilter.of(tr => {
    if (!tr.docChanged || characterLimit <= 0) {
      return true;
    }
    const docLength = tr.newDoc.toString().length;
    return docLength <= characterLimit;
  });
}
